import { differenceInDays } from 'date-fns';

import { stripHtml } from '@/utils';

export default class Attachment {
  constructor(attachment) {
    // Static attributes
    this.contentSourceId = attachment.contentSourceId;
    this.createdAt = attachment.createdAt;
    this.difficultyLowerBound = attachment.difficultyLowerBound;
    this.difficultyUpperBound = attachment.difficultyUpperBound;
    this.displayAttachmentId = attachment.displayAttachmentId;
    this.displayDescription = attachment.displayDescription;
    this.displayTitle = attachment.displayTitle;
    this.id = attachment.id;
    this.metadata = attachment.metadata;
    this.productAssociations = attachment.productAssociations;
    this.rawName = attachment.name;
    this.tags = attachment.tags;
    this.thumbnails = attachment.thumbnails;
    this.type = attachment.type;
    this.updatedAt = attachment.updatedAt;
    this.unlockedBy = attachment.unlockedBy;
    this.url = attachment.url;

    // Pseudo-private attributes
    this._locked = null;
    this._categoryIds = null;
  }

  static formatDuration(duration) {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    const secondString = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}:${secondString}`;
  }

  get assetDescription() {
    if (this.displayDescription) {
      return stripHtml(this.displayDescription);
    }
  }

  get assetTitle() {
    let title = this.displayTitle || this.name || 'Attachment';
    return stripHtml(title);
  }

  get assetType() {
    return 'Attachment';
  }

  get categoryIds() {
    if (!this._categoryIds) {
      this._categoryIds = this.productAssociations?.map(pa => pa.categoryId).filter(categoryId => categoryId);
    }
    return this._categoryIds;
  }

  get duration() {
    const duration = this.metadata && this.metadata.duration;
    if (duration) {
      return this.constructor.formatDuration(duration);
    }
  }

  get escapedName() {
    return stripHtml(this.name);
  }

  get icon() {
    if (this.mediaType === 'image') {
      return 'photo';
    } else if (['audio', 'video', 'biodigital'].includes(this.mediaType)) {
      return this.mediaType;
    } else {
      return 'play';
    }
  }

  get libraryRoute() {
    return {
      name: 'library#show',
      params: {
        assetType: 'attachment',
        assetId: this.id,
      },
    };
  }

  get listRowType() {
    return 'attachment-list-row';
  }

  get mediaType() {
    if (this.type.match(/^audio\//)) {
      return 'audio';
    } else if (this.type.match(/^video\//)) {
      return 'video';
    } else if (this.type.match(/^image\//)) {
      return 'image';
    } else if (this.type.match(/humankit/)) {
      return 'biodigital';
    } else {
      return 'video';
    }
  }

  get name() {
    let name = this.rawName;
    // Biodigital attachments get their title from meta data but we want to fallback to the attachment name if contentTitle is not present
    if (this.mediaType === 'biodigital' && this.metadata.contentTitle) {
      name = this.metadata.contentTitle;
    }

    return name;
  }

  get smallThumbnail() {
    if (this.mediaType === 'biodigital') {
      return this.metadata.contentThumbnailUrl;
    } else if (this.mediaType === 'image') {
      // If the attachment is an image, we can allow it to fallback to itself as a thumbnail
      return this.thumbnails?.smallPreservedRatio || this.thumbnails?.small || this.url;
    } else {
      return this.thumbnails?.smallPreservedRatio || this.thumbnails?.small;
    }
  }

  get largeThumbnail() {
    if (this.mediaType === 'biodigital') {
      return this.metadata.contentThumbnailUrl;
    } else if (this.mediaType === 'image') {
      // If the attachment is an image, we can allow it to fallback to itself as a thumbnail
      return this.thumbnails?.large || this.url;
    } else {
      return this.thumbnails?.large;
    }
  }

  get createdAtText() {
    if (this.createdAt) {
      const daysAgo = differenceInDays(new Date(), new Date(this.createdAt));
      if (daysAgo < 1) {
        return 'Today';
      } else if (daysAgo === 1) {
        return 'Yesterday';
      } else if (daysAgo > 1 && daysAgo < 14) {
        return `${daysAgo} days ago`;
      } else if (daysAgo >= 14 && daysAgo < 365) {
        const weeksAgo = Math.floor(daysAgo / 7);
        return `${weeksAgo} weeks ago`;
      } else if (daysAgo >= 365) {
        const yearsAgo = Math.floor(daysAgo / 365);
        if (yearsAgo === 1) {
          return 'A year ago';
        } else {
          return `${yearsAgo} years ago`;
        }
      }
    }
  }

  cardMetadata(store) {
    if (this.mediaType === 'video' || this.mediaType === 'audio') {
      const attachmentState = store.getters['attachmentStates/get'][this.id];
      if (attachmentState) {
        const remainingDuration = this.metadata?.duration - attachmentState.currentTimestamp;
        if (remainingDuration && remainingDuration > 2) {
          return `${Attachment.formatDuration(remainingDuration)} Remaining`;
        }
      }
      return this.duration;
    } else if (this.mediaType === 'biodigital') {
      return '3D Anatomy';
    } else if (this.mediaType === 'image') {
      return 'Image';
    }
  }

  completedState(state) {
    if (state) {
      // We need a buffer to account for a user having a short
      // amount of time left in a video but is essentially completed
      // This will represent that duration in seconds
      const buffer = 2;
      return state.currentTimestamp >= (Math.floor(this.metadata.duration) - buffer);
    }
    return false;
  }

  difficultyLabel(store) {
    if (this.difficultyLowerBound) {
      const allLevels = store.getters['difficultyLevels/all'];
      const level = allLevels.find(l => l.difficultyLevel == this.difficultyLowerBound);
      return level?.label;
    }
  }

  locked(access, store) {
    if (this._locked !== null) {
      return this._locked;
    }
    let accessible = access(store, 'attachment', this.id);
    this._locked = !accessible;
    return !accessible;
  }

  state(store) {
    return store.getters['attachmentStates/get'][this.id];
  }

  sortOrder(categoryIds) {
    const productAssociation = this.productAssociations.find(productAssociation => categoryIds.includes(productAssociation.categoryId));
    // if order is null or 0, return 1 so that null, 0 and 1 will effectively be treated as the same
    return productAssociation?.order || 1;
  }
}
